import { useParams } from "next/navigation";
import { useLocale } from "next-intl";
import { createNavigation } from "next-intl/navigation";
import { routing } from "./routing";

/**
 * Localized navigation primitives. Import these instead of next/link or
 * next/navigation in chrome components (Header, Footer, LanguageSwitch,
 * NavLink) so that link `href` values can be passed as internal pathnames
 * (e.g. "/chi-sono") and next-intl will rewrite to the locale-specific
 * external URL automatically (e.g. /en/about).
 */
export const { Link, redirect, usePathname, useRouter, getPathname } =
  createNavigation(routing);

/**
 * Public URL pathname (e.g. "/", "/en/about"), identical during prerender and
 * in the browser. `usePathname` from next/navigation returns the path rewritten
 * by the proxy on statically prerendered pages ("/it", "/en/chi-sono") but the
 * browser URL on the client: anything rendered from it breaks hydration
 * (React #418). next-intl's internal pathname is stable on both sides, so we
 * derive the public URL from it.
 */
export function usePublicPathname(): string {
  const pathname = usePathname();
  const locale = useLocale();
  const params = useParams();
  return getPathname({
    locale,
    href: { pathname, params },
  } as Parameters<typeof getPathname>[0]);
}
